import React from 'react';

import { BarChart, ChartProvider } from '@ihme/common/web/components/';
import _ from '@ihme/common/locale';
import styled from '@ihme/common/theme/styled';
import getTitleAndSubtitle from '@ihme/common/web/utility/echarts-helpers/get-adopted-title-and-subtitle';

import api from '../../../api';
import config from '../../../config';

import { renderChartCopyright } from '../../../utility/chart-copyright';
import formatChartLabel from '../../../utility/formatting/formatChartLabel';
import formatChartValue from '../../../utility/formatting/formatChartValue';

import echartsTheme from '../../../theme/echarts';
import { pngIcon } from '../../../theme/icons';

const BarChartContainer = styled.div(({ theme }) => ({
    height: 590,
    [theme.breakpoint.xs]: {
        height: 790,
    },
}));

const DEFAULT_FILTERS = {
    metric_id: config.defaultFactsheetsRisksMetricId,
    measure_id: config.defaultFactsheetsRisksMeasureId,
    age_group_id: config.defaultFactsheetsRisksAgeGroupId,
    cause_id: config.defaultFactsheetsRisksCauseId,
    year: config.defaultFactsheetsYear,
};

type Props = {
    locationId: number;
};

class Risks extends React.Component<Props> {
    renderTitle = (filters) =>
        _('factsheets_risks_chart_title', {
            measure: _(`measure_${filters.measure_id}`),
            metric: _(`metric_${filters.metric_id}`),
            year: filters.year,
        });

    renderSubtitle = (filters) =>
        this.getLocalizedFilter(filters.location_id, 'location_') +
        ', ' +
        _(`age_group_${filters.age_group_id}`) +
        ', DALYs Rate/100K';

    renderTooltip = (params, { isMobileView }) => {
        const { columns, record } = params.data.rawData;

        const getRecordValue = (key: string): string | number => {
            const idx = columns.indexOf(key);
            const recordValue = record[idx];

            return (key.includes('_id'))
                ? _(`${key.slice(0, -3)}_${recordValue}`)
                : recordValue;
        };

        const year = getRecordValue('year');
        const risk = getRecordValue('rei_id');
        const measure = getRecordValue('measure_id');
        const metric = getRecordValue('metric_id');
        const ageGroup = getRecordValue('age_group_id');
        const location = getRecordValue('location_id');
        const value = formatChartValue(getRecordValue('value'));
        const lower = formatChartValue(getRecordValue('lower'));
        const upper = formatChartValue(getRecordValue('upper'));

        const uncertainty = upper !== null && lower !== null ? `(${lower} - ${upper})` : '';
        const valueWithUncertainty = `${value} ${uncertainty}`;

        return (
            `<div class='chart-tooltip-text'>
                <div class='chart-tooltip-title'>${year}</div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        Risk:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${risk}`}
                    </span>
                </div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        Location:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${location}`}
                    </span>
                </div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        Age:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${ageGroup}`}
                    </span>
                </div>
                <div>
                    <span class='chart-tooltip-text-bold'>
                        ${measure} ${metric}:
                    </span>
                    ${isMobileView ? `<br/>` : ''}
                    ${` ${valueWithUncertainty}`}
                </div>
            </div>`
        );
    };

    getSaveFilename = ({ filters: { measure_id, metric_id, location_id, age_group_id, year } }) =>
        'Factsheets, Cardiovascular Diseases Risks Breakdown' +
        this.getLocalizedFilter(measure_id, 'measure_', ', ') +
        this.getLocalizedFilter(metric_id, 'long_metric_', ', ') +
        this.getLocalizedFilter(location_id, 'location_', ' in ') +
        this.getLocalizedFilter(age_group_id, 'age_group_', ', ') +
        ' in ' +
        year;

    getLocalizedFilter = (
        value: string | number,
        localePrefix: string,
        prefix: string = ''
    ): string => {
        const localize = (source): string => source
            ? source.toString()
                .split(',')
                .map(id => _(`${localePrefix}${id}`))
                .join(',')
            : '';

        return prefix +
            value.toString()
                .split(',')
                .map(el => localize(el))
                .join(', ');
    };

    renderYAxisName = ({ filters: { metric_id, measure_id } }) =>
        _(`measure_${measure_id}_full_name`) + ', ' + _(`metric_${metric_id}`);

    render() {
        const { locationId } = this.props;

        const filters = {
            ...DEFAULT_FILTERS,
            location_id: `${config.globalLocationId},${locationId}`,
        };

        return (
            <ChartProvider
                key={locationId.toString()}
                loadFilters={() => Promise.resolve({})}
                loadData={api.data.getRiskAttribution}
                defaultFilters={filters}
            >
                <BarChartContainer>
                    <BarChart
                        theme={echartsTheme}
                        configureLegend={({ isMobileView }) => ({
                            ...echartsTheme.bar.legend,
                            ...(isMobileView && {
                                orient: 'vertical',
                            }),
                            top: 460,
                        })}
                        configureTitles={({ filters, chartWidth }) => (
                            [
                                getTitleAndSubtitle(
                                    this.renderTitle(filters),
                                    this.renderSubtitle(filters),
                                    chartWidth,
                                    document.getElementById('canvasFontSizer'),
                                    echartsTheme.title
                                ),
                            ]
                        )}
                        configureTooltip={() => ({ position: 'auto' })}
                        renderGraphic={renderProps =>
                            renderChartCopyright(renderProps, 76, renderProps.isMobileView ? 338 : 140)
                        }
                        mobileViewWidth={680}
                        renderTooltip={this.renderTooltip}
                        xAxisKey="location_id"
                        formatXAxisLabel={id => _(`location_${id}`)}
                        yAxisKey="value"
                        formatYAxisLabel={formatChartLabel}
                        renderYAxisName={this.renderYAxisName}
                        dataKey="rei_id"
                        multipleChartByKey="gender_id"
                        multipleChartByKeyIds={[config.femaleGenderId]}
                        formatLegendLabel={id => _(`rei_${id}`)}
                        saveAsImage={{
                            visible: true,
                            filename: this.getSaveFilename,
                            icon: pngIcon,
                        }}
                    />
                    <canvas id="canvasFontSizer" width="0" height="0" />
                </BarChartContainer>
            </ChartProvider>
        );
    }
}

export default Risks;
